/**
 * Mobile horizontal scroll for the plugin builder editor's two-pane layout.
 *
 * On narrow viewports the conversation and preview panes are laid out
 * side-by-side inside a horizontally-scrollable container. This hook tracks
 * scroll state, snaps the container to whichever pane is >50% visible when
 * scrolling stops, and exposes two imperative handles for a custom
 * scrollbar and pane-button navigation.
 *
 * Returned values:
 *   - panelsContainerRef: attach to the scrollable pane container
 *   - scrollTrackRef: attach to the custom scrollbar track
 *   - scrollState: live {scrollLeft, scrollWidth, clientWidth} for rendering
 *     a custom scrollbar thumb position
 *   - handleScrollbarDrag: imperative scroll-to-clientX for thumb dragging
 *   - scrollToPanel: imperative scroll-to-{'left'|'right'} for nav buttons
 *   - isDraggingRef: set to true during a drag so the snap-on-scroll-end is
 *     suppressed while the user is actively dragging
 *
 * Extracted from PluginBuilderPage.tsx during PR 12.
 */

import { useCallback, useEffect, useRef, useState, MutableRefObject } from 'react';

export interface MobileScrollState {
  scrollLeft: number;
  scrollWidth: number;
  clientWidth: number;
}

export interface UseMobileHorizontalScrollResult {
  panelsContainerRef: MutableRefObject<HTMLDivElement | null>;
  scrollTrackRef: MutableRefObject<HTMLDivElement | null>;
  scrollState: MobileScrollState;
  handleScrollbarDrag: (clientX: number) => void;
  scrollToPanel: (panel: 'left' | 'right') => void;
  isDraggingRef: MutableRefObject<boolean>;
}

export function useMobileHorizontalScroll(
  /** Re-run the scroll listener setup when this flips false (i.e. when initial loading finishes). */
  isLoading: boolean,
): UseMobileHorizontalScrollResult {
  const panelsContainerRef = useRef<HTMLDivElement | null>(null);
  const scrollTrackRef = useRef<HTMLDivElement | null>(null);
  const isDraggingRef = useRef(false);
  const snapTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [scrollState, setScrollState] = useState<MobileScrollState>({
    scrollLeft: 0,
    scrollWidth: 0,
    clientWidth: 0,
  });

  useEffect(() => {
    const container = panelsContainerRef.current;
    if (!container) return;

    const updateScrollState = () => {
      setScrollState({
        scrollLeft: container.scrollLeft,
        scrollWidth: container.scrollWidth,
        clientWidth: container.clientWidth,
      });
    };

    const scheduleSnap = () => {
      if (isDraggingRef.current) return;
      if (snapTimeoutRef.current) clearTimeout(snapTimeoutRef.current);
      snapTimeoutRef.current = setTimeout(() => {
        const maxScroll = container.scrollWidth - container.clientWidth;
        if (maxScroll <= 0) return;
        const ratio = container.scrollLeft / maxScroll;
        const target = ratio > 0.5 ? maxScroll : 0;
        container.scrollTo({ left: target, behavior: 'smooth' });
      }, 150);
    };

    const handleScroll = () => {
      updateScrollState();
      scheduleSnap();
    };

    updateScrollState();

    container.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', updateScrollState);
    const resizeObserver = new ResizeObserver(updateScrollState);
    resizeObserver.observe(container);

    return () => {
      container.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', updateScrollState);
      resizeObserver.disconnect();
      if (snapTimeoutRef.current) clearTimeout(snapTimeoutRef.current);
    };
  }, [isLoading]);

  const handleScrollbarDrag = useCallback((clientX: number) => {
    const container = panelsContainerRef.current;
    const track = scrollTrackRef.current;
    if (!container || !track) return;

    const trackRect = track.getBoundingClientRect();
    const trackWidth = trackRect.width;
    const thumbWidth = 24;
    const maxThumbLeft = trackWidth - thumbWidth;

    const relativeX = clientX - trackRect.left - thumbWidth / 2;
    const clampedX = Math.max(0, Math.min(relativeX, maxThumbLeft));

    const scrollRatio = clampedX / maxThumbLeft;
    const maxScrollLeft = container.scrollWidth - container.clientWidth;
    container.scrollLeft = scrollRatio * maxScrollLeft;
  }, []);

  const scrollToPanel = useCallback((panel: 'left' | 'right') => {
    const container = panelsContainerRef.current;
    if (!container) return;
    const target = panel === 'left' ? 0 : container.scrollWidth - container.clientWidth;
    container.scrollTo({ left: target, behavior: 'smooth' });
  }, []);

  return {
    panelsContainerRef,
    scrollTrackRef,
    scrollState,
    handleScrollbarDrag,
    scrollToPanel,
    isDraggingRef,
  };
}
